import { shownTime } from './round-config'
import { parkingHours } from './parking-hours'

// Parking as picture tiles (Liam, 27 Oct 2026, design B: https://claude.ai/artifact/7wvAW2a5nPFmLFjKZCkUUw, checked twice
// by Astra). Still one customer field, customers.parking_restrictions, so every screen, import, export and older phone
// keeps reading it; the tiles write it in one fixed shape Roundly reads back exactly:
//   "Pay and display. Permit only. No parking 8am to 9:30am Mon to Fri; 4pm to 6:30pm every day. Park in the side road."
// Words that are not in that shape (typed before, or imported) are read as before (parking-hours.ts, one range) and kept
// as "Anything else", so nothing anyone wrote is thrown away. Pure, so the checks run it without a page.

export interface ParkingRange { from: string; to: string; days: number[] | null } // days: 1 Mon .. 7 Sun; null every day
export interface ParkingRules { easy: boolean; permit: boolean; pay: boolean; ranges: ParkingRange[]; rest: string }

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const pad = (n: number): string => String(n).padStart(2, '0')
const mins = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5))

function clock(t: string): string | null {
  const m = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i.exec(t.trim())
  if (!m) return null
  let h = Number(m[1]); const mi = m[2] ? Number(m[2]) : 0
  if (h < 1 || h > 12 || mi > 59) return null
  const pm = m[3]!.toLowerCase() === 'pm'
  if (h === 12) h = pm ? 12 : 0; else if (pm) h += 12
  return `${pad(h)}:${pad(mi)}`
}
// The 12-hour words the fixed shape always uses, whatever the round's own clock ("11am", "9:30am").
function clockWords(hhmm: string): string {
  let h = Number(hhmm.slice(0, 2)); const mi = hhmm.slice(3, 5)
  const half = h >= 12 ? 'pm' : 'am'
  h = h % 12 || 12
  return `${h}${mi === '00' ? '' : `:${mi}`}${half}`
}

export function daysWords(days: number[] | null): string {
  if (!days || days.length === 7) return 'every day'
  const s = [...days].sort((a, b) => a - b)
  if (s.join() === '1,2,3,4,5') return 'Mon to Fri'
  if (s.join() === '6,7') return 'Sat and Sun'
  return s.map((d) => DAY[d - 1]).join(', ')
}
function daysOf(words: string): number[] | null | undefined {
  const w = words.trim().replace(/\.$/, '')
  if (!w || /^every day$/i.test(w)) return null
  if (/^mon to fri$/i.test(w)) return [1, 2, 3, 4, 5]
  if (/^sat and sun$/i.test(w)) return [6, 7]
  const list = w.split(/\s*,\s*/).map((d) => DAY.findIndex((x) => x.toLowerCase() === d.toLowerCase()) + 1)
  return list.length && list.every((d) => d > 0) ? list : undefined
}

const NO_PARKING = /No parking ((?:\d{1,2}(?::\d{2})?(?:am|pm) to \d{1,2}(?::\d{2})?(?:am|pm)(?: [A-Za-z ,]+?)?)(?:; (?:\d{1,2}(?::\d{2})?(?:am|pm) to \d{1,2}(?::\d{2})?(?:am|pm)(?: [A-Za-z ,]+?)?))*)\.(?:\s|$)/
const PERMIT = /\b(?:permit|residents?'? (?:only|bays?|parking))\b/i
const PAY = /\b(?:pay (?:and|&) display|pay by phone|paybyphone|ringgo|meters?|metered|pay to park)\b/i
const EASY = /^\s*(?:easy|easy, no limits|easy parking|no restrictions|park outside any time)\.?\s*$/i

export function parkingRules(text: string | null | undefined): ParkingRules {
  const out: ParkingRules = { easy: false, permit: false, pay: false, ranges: [], rest: '' }
  let t = (text ?? '').trim()
  if (!t) return out
  if (EASY.test(t)) return { ...out, easy: true }
  // The fixed shape: its sentences taken off the front, whatever is left is Anything else.
  let fixed = false
  for (;;) {
    if (/^Pay and display\.(\s|$)/.test(t)) { out.pay = true; t = t.slice('Pay and display.'.length).trim(); fixed = true; continue }
    if (/^Permit only\.(\s|$)/.test(t)) { out.permit = true; t = t.slice('Permit only.'.length).trim(); fixed = true; continue }
    const m = NO_PARKING.exec(t)
    if (m && m.index === 0) {
      for (const part of m[1]!.split('; ')) {
        const r = /^(\S+) to (\S+?)(?: (.+))?$/.exec(part)
        const from = r && clock(r[1]!), to = r && clock(r[2]!)
        const days = r ? daysOf(r[3] ?? '') : undefined
        if (from && to && days !== undefined && mins(from) < mins(to)) out.ranges.push({ from, to, days })
      }
      t = t.slice(m[0].length).trim(); fixed = true; continue
    }
    break
  }
  out.rest = t
  if (fixed) return out
  // Words from before: what can be read, the words kept as they are.
  out.permit = PERMIT.test(t)
  out.pay = PAY.test(t)
  const h = parkingHours(t)
  if (h) out.ranges.push({ from: h.from, to: h.to, days: null })
  return out
}

// The tiles' answer as the field's words. Anything else stays at the end, as typed.
export function parkingText(r: ParkingRules): string {
  if (r.easy && !r.rest.trim()) return 'Easy, no limits.'
  const bits: string[] = []
  if (r.pay) bits.push('Pay and display.')
  if (r.permit) bits.push('Permit only.')
  const ranges = r.ranges.filter((x) => mins(x.from) < mins(x.to))
  if (ranges.length) bits.push(`No parking ${ranges.map((x) => `${clockWords(x.from)} to ${clockWords(x.to)} ${daysWords(x.days)}`).join('; ')}.`)
  const rest = r.rest.trim()
  if (rest) bits.push(/[.!?]$/.test(rest) ? rest : `${rest}.`)
  return bits.join(' ')
}

// "No parking 11am to 1pm, Mon to Fri", "Permit only", "Pay and display": the line on a job, the round's clock.
export function parkingHeadline(r: ParkingRules): string {
  const ranges = r.ranges.map((x) => `${shownTime(x.from)} to ${shownTime(x.to)}${x.days ? `, ${daysWords(x.days)}` : ''}`)
  if (ranges.length) return `No parking ${ranges.join('; ')}`
  if (r.permit) return 'Permit only'
  if (r.pay) return 'Pay and display'
  if (r.easy) return 'Easy, no limits'
  return r.rest
}

// ISO weekday of a yyyy-mm-dd, 1 Mon .. 7 Sun.
const weekday = (date: string): number => { const d = new Date(`${date}T12:00:00Z`).getUTCDay(); return d === 0 ? 7 : d }

// The ranges a visit on this day at this time (start to finish, when its length is known) runs into. With no date,
// every range counts; with no time, none can be judged (the caller says "no time yet" instead).
export function rangesHit(r: ParkingRules, date: string | null | undefined, slot: string | null | undefined, lengthMinutes: number | null = null): ParkingRange[] {
  const m = slot ? /^\s*(\d{1,2}):(\d{2})/.exec(slot) : null
  if (!m) return []
  const start = Number(m[1]) * 60 + Number(m[2])
  const end = lengthMinutes && lengthMinutes > 0 ? start + lengthMinutes : start + 1
  const day = date ? weekday(date) : null
  return r.ranges.filter((x) => (day === null || !x.days || x.days.includes(day)) && start < mins(x.to) && end > mins(x.from))
}

// Red on a job: a time in the hours, no time yet with hours to keep out of, or permit only (always worth knowing).
export function parkingIsWarning(r: ParkingRules, date: string | null | undefined, slot: string | null | undefined, lengthMinutes: number | null = null): boolean {
  if (r.permit) return true
  if (!r.ranges.length) return false
  if (!slot) return true
  return rangesHit(r, date, slot, lengthMinutes).length > 0
}

export const hasParking = (r: ParkingRules): boolean => r.easy || r.permit || r.pay || r.ranges.length > 0 || r.rest.trim().length > 0
